import {
  CartLineProvider,
  CartShopPayButton,
  ProductOptionsProvider,
  useCart,
  Image,
  Link,
} from '@shopify/hydrogen';
import {CartLine, Product} from '@shopify/hydrogen/storefront-api-types';
import {useEffect, useRef, useState} from 'react';
import {X} from 'react-feather';

import {
  Button,
  CartEmpty,
  CartLineItem,
  CartLineItemDetail,
  useDrawerContext,
} from '~/components';
import {queryApi} from '~/utils/queryAPI';
import {useQuery} from 'react-query';
import clsx from 'clsx';
import Carousel from 'nuka-carousel';
import {useWindowSize} from '~/hooks';
import {formatNumber} from '~/utils';

export function CartDetails({onClose}: {onClose?: () => void}) {
  const {lines} = useCart();
  const [selectedLine, setSelectedLine] = useState<CartLine | null>(null);

  const scrollRef = useRef(null);

  const {
    data: selectedProduct,
    isLoading: isLoadingSelectedProduct,
    isError: isSelectedProductError,
    //Is react-query
    // eslint-disable-next-line hydrogen/client-component-banned-hooks
  } = useQuery(
    ['product-by-handle', selectedLine],
    () =>
      selectedLine &&
      queryApi({
        url: '/api/product-by-handle',
        variables: {
          handle: selectedLine.merchandise.product.handle,
        },
      }).then((r) => r.json()),
  );

  if (lines.length === 0) {
    return <CartEmpty onClose={onClose} />;
  }
  const actualCartItems = lines.filter(
    (line) => line.merchandise.product.handle !== 'surge-fee',
  );

  return selectedLine && selectedProduct ? (
    <ProductOptionsProvider
      data={selectedProduct}
      initialVariantId={selectedLine.merchandise.id}
    >
      <CartLineProvider line={selectedLine}>
        <CartLineItemDetail
          handleClose={() => {
            setSelectedLine(null);
          }}
        />
      </CartLineProvider>
    </ProductOptionsProvider>
  ) : (
    <form className="flex h-full flex-col justify-between gap-4">
      <section
        ref={scrollRef}
        aria-labelledby="cart-contents"
        className="flex flex-col gap-4"
      >
        <div className="flex items-center justify-between text-acai-900">
          <div className="flex items-center gap-4">
            <button type="button" onClick={onClose}>
              <X width={24} height={24} className="" />
            </button>
            <div className="text-xl">Your Cart</div>
          </div>
          <div>{actualCartItems.length} item(s)</div>
        </div>
        <ul className="flex max-h-fit flex-col gap-4 overflow-y-scroll md:gap-6">
          {lines.map((line) => {
            return (
              <CartLineProvider key={line.id} line={line}>
                <CartLineItem
                  onEditItem={(lineId) => {
                    const line = lines.find((value) => value.id === lineId);
                    setSelectedLine(line as CartLine);
                  }}
                  isLoading={
                    line.id === selectedLine?.id && isLoadingSelectedProduct
                  }
                  isError={
                    line.id === selectedLine?.id && isSelectedProductError
                  }
                />
              </CartLineProvider>
            );
          })}
        </ul>
      </section>
      <section
        aria-labelledby="summary-heading"
        className="flex flex-col gap-4"
      >
        <AddonRefresher />
        <CartCheckoutActions />
      </section>
    </form>
  );
}

function CartCheckoutActions() {
  const {checkoutUrl} = useCart();

  return (
    <div className="flex flex-col gap-4 pb-12 md:pb-0">
      <div className="text-[10px] md:text-sm">
        By checking out, you agree to our{' '}
        <a href="/terms-of-service" className="underline">
          Terms of Service
        </a>{' '}
        and{' '}
        <a href="/privacy-policy" className="underline">
          Privacy Policy
        </a>
        .
      </div>
      <Button url={checkoutUrl} className="w-full" label="Checkout" />
      <div className="relative w-full pt-3">
        <div className="absolute left-1/2 top-[1px] -translate-x-1/2 bg-foam-100 px-2 text-[10px] xs:text-[12px] sm:text-sm">
          Express Checkout
        </div>
        <div className="flex gap-2 border p-4">
          <CartShopPayButton className="w-full [&>shop-pay-button]:inline-table [&>shop-pay-button]:w-full" />
        </div>
      </div>
    </div>
  );
}

function AddonRefresher() {
  const {lines} = useCart();

  const [slideIndex, setSlideIndex] = useState(0);
  const windowWidth = useWindowSize()?.width ?? 0;
  const [isLoading, setLoading] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);

  const getRecommendation = async () => {
    setLoading(true);
    const productRecommendationData: Product[] = [];
    for (const line of lines) {
      const productRes = await queryApi({
        url: '/api/product-by-handle',
        variables: {
          handle: line.merchandise.product.handle,
        },
      });
      const productResData = (await productRes.json()) as Product;
      const productRecommendationRes = await queryApi({
        url: '/api/product-recommendation',
        variables: {
          productId: productResData.id,
        },
      });
      const productRecommendationResData =
        (await productRecommendationRes.json()) as Product[];

      for (const d of productRecommendationResData) {
        if (!productRecommendationData.find((e) => e.id === d.id)) {
          productRecommendationData.push(d);
        }
      }
    }

    setProducts(productRecommendationData);
    setLoading(false);
  };

  useEffect(() => {
    getRecommendation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between text-start text-sm sm:text-xl">
        <div>Our community also loves</div>
        <div className="flex gap-2">
          <button
            onClick={(e) => {
              e.preventDefault();
              setSlideIndex((prev) => prev - (windowWidth < 600 ? 1 : 2));
            }}
            className={clsx(
              'flex items-center justify-center bg-transparent p-1',
            )}
          >
            <Image
              src="/images/prev-arrow.svg"
              alt="tfr-loop"
              width={10}
              height={6}
            />
          </button>
          <button
            onClick={(e) => {
              e.preventDefault();
              setSlideIndex((prev) => prev + (windowWidth < 600 ? 1 : 2));
            }}
            className={clsx(
              'flex items-center justify-center bg-transparent p-1',
            )}
          >
            <Image
              src="/images/next-arrow.svg"
              alt="tfr-loop"
              width={10}
              height={6}
            />
          </button>
        </div>
      </div>
      <div className="pb-2 md:w-[38rem]">
        <div className="cart-product-recommendation flex gap-4">
          <Carousel
            autoplay={false}
            wrapAround={true}
            renderCenterLeftControls={null}
            renderCenterRightControls={null}
            renderBottomCenterControls={null}
            slideIndex={slideIndex}
            slidesToShow={windowWidth < 600 ? 1 : 2}
            slidesToScroll={windowWidth < 600 ? 1 : 2}
            cellSpacing={10}
          >
            {products?.length ?? 0 > 0 ? (
              products?.map((product) => (
                <OptionItem key={product.id} product={product} />
              ))
            ) : (
              <div className="flex w-full items-center justify-center p-8 text-center">
                {isLoading ? (
                  <Image
                    src="/tfr-loop.gif"
                    alt="tfr-loop"
                    className="animate-bounce"
                    width={60}
                    height={60}
                  />
                ) : (
                  <>No recommendation</>
                )}
              </div>
            )}
          </Carousel>
        </div>
      </div>
    </div>
  );
}

function OptionItem({product}: {product: Product}) {
  const {setIsOpenCart} = useDrawerContext();
  const productTitleArr = (product?.title ?? 'Product Title').split(' ');
  const productTypeArr = (product.productType ?? 'Product Type').split(' | ');

  return (
    <Link
      to={`/products/${product.handle}`}
      onClick={() => setIsOpenCart(false)}
    >
      <div className="flex gap-4 text-acai-900">
        <div className="flex-shrink">
          <div className="aspect-square h-16 w-16 xs:h-24 xs:w-24 md:h-28 md:w-28">
            <Image
              width={112}
              height={112}
              widths={[112]}
              data={product.images.nodes.at(0) ?? {url: 'https://404.local'}}
              loaderOptions={{
                scale: 2,
                crop: 'center',
              }}
              className="aspect-square h-16 w-16 rounded-xl border object-cover object-center xs:h-24 xs:w-24 md:h-28 md:w-28"
            />
          </div>
        </div>
        <div className="flex w-max flex-grow justify-between">
          <div className="flex flex-col justify-between text-start">
            <div className="">
              <div className="text-sm xs:text-lg">
                {productTitleArr[0]}
                {productTitleArr.length > 1 && (
                  <>
                    {' '}
                    <span className="font-serif italic">
                      {productTitleArr
                        .filter((_, index) => index !== 0)
                        .join(' ')}
                    </span>
                  </>
                )}
              </div>
              <div className="break-words text-[12px] xs:text-sm">
                {productTypeArr.at(1) || product.productType}
              </div>
              <div className="text-[12px] font-bold text-acai-900 xs:text-sm">
                {`From ${formatNumber(
                  product?.variants?.nodes?.at(0)?.priceV2?.amount as string,
                )} ${product?.variants?.nodes?.at(0)?.priceV2?.currencyCode}`}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}
